'use client';

import { useState, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { Search, Package } from 'lucide-react';
import { categories, getItemsByCategory, searchItems } from '../data/mock-data';
import type { POSItem } from '../types';
import { formatCurrency } from '../../../types';

interface ItemSelectorProps {
  onSelectItem: (item: POSItem) => void;
}

export function ItemSelector({ onSelectItem }: ItemSelectorProps) {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredItems = useMemo(() => {
    if (searchQuery.trim()) {
      return searchItems(searchQuery);
    }
    return getItemsByCategory(selectedCategory);
  }, [selectedCategory, searchQuery]);

  return (
    <div className="flex flex-col h-full">
      {/* 검색 바 */}
      <div className="p-4 border-b border-border">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="상품명, 코드, 바코드 검색..."
            className="w-full h-10 pl-10 pr-4 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      </div>

      {/* 카테고리 탭 */}
      <div className="flex border-b border-border">
        {categories.map((category) => (
          <button
            key={category.id}
            onClick={() => {
              setSelectedCategory(category.id);
              setSearchQuery('');
            }}
            className={cn(
              'flex items-center gap-1 px-4 py-3 text-sm font-medium border-b-2 transition-all whitespace-nowrap',
              selectedCategory === category.id && !searchQuery
                ? 'border-primary text-primary bg-primary/5'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-accent'
            )}
          >
            {category.name}
            <span className={cn(
              'px-1.5 py-0.5 text-xs rounded',
              selectedCategory === category.id && !searchQuery
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground'
            )}>
              {category.itemCount}
            </span>
          </button>
        ))}
      </div>

      {/* 상품 그리드 */}
      <div className="flex-1 overflow-y-auto p-4">
        {filteredItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
            <Package className="w-12 h-12 mb-2 opacity-50" />
            <p>검색 결과가 없습니다</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {filteredItems.map((item) => (
              <ItemCard
                key={item.id}
                item={item}
                onClick={() => onSelectItem(item)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface ItemCardProps {
  item: POSItem;
  onClick: () => void;
}

function ItemCard({ item, onClick }: ItemCardProps) {
  const isLowStock = item.stock <= 10;

  return (
    <button
      onClick={onClick}
      disabled={item.stock === 0}
      className={cn(
        'flex flex-col p-3 rounded-lg border border-border bg-card text-left transition-all',
        'hover:border-primary hover:shadow-md',
        item.stock === 0 && 'opacity-50 cursor-not-allowed'
      )}
    >
      {/* 상품 아이콘/이미지 영역 */}
      <div className="w-full aspect-square rounded-lg bg-muted flex items-center justify-center mb-2">
        <Package className="w-8 h-8 text-muted-foreground" />
      </div>

      {/* 상품 정보 */}
      <p className="text-xs text-muted-foreground">{item.code}</p>
      <p className="font-medium text-sm line-clamp-2 min-h-[2.5rem]">{item.name}</p>
      <p className="text-primary font-bold mt-1">{formatCurrency(item.price)}</p>

      {/* 재고 표시 */}
      <p
        className={cn(
          'text-xs mt-1',
          item.stock === 0
            ? 'text-red-500'
            : isLowStock
              ? 'text-yellow-500'
              : 'text-muted-foreground'
        )}
      >
        재고: {item.stock}{item.unit}
        {item.stock === 0 && ' (품절)'}
      </p>
    </button>
  );
}
